// src/components/domains/advisor-portal/client-profile/LifetimeFinancialAnalysis.js
import React, { useRef, useEffect, useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import "chartjs-adapter-moment";
import moment from "moment";
import {
  Box,
  Card,
  IconButton,
  Tooltip as MuiTooltip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestoreIcon from "@mui/icons-material/Restore";
import api from "../../../../../utils/apiConfig";

import logger from '../../../../../utils/logger';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler, // enables area fills
  annotationPlugin, // register the annotation plugin
);

const COLORS = {
  income: "#38B2AC", // teal
  expenses: "#F56565", // coral
  surplus: "#68D391", // mint green
  assets: "#48BB78", // green
  assetsFill: "rgba(72,187,120,0.2)",
  debts: "#E53E3E", // red
  debtsFill: "rgba(229,62,62,0.2)",
  netWorth: "#2C5282", // navy
  confidence: "#90CDF4", // light blue
  divider: "#718096", // gray-600
};

export default function LifetimeFinancialAnalysis({ goals = [], householdId }) {
  const chartRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState("all");
  const [timeUnit, setTimeUnit] = useState("month");
  const [todayPct, setTodayPct] = useState(0);
  const [lastNames, setLastNames] = useState([]);
  const [visible, setVisible] = useState({
    income: true,
    expenses: true,
    surplus: true,
    assets: true,
    debts: true,
    netWorth: true,
  });

  // Fetch last names when householdId changes
  useEffect(() => {
    const fetchLastNames = async () => {
      try {
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/goals-legacy/last-names`,
        );
        if (response.data && Array.isArray(response.data)) {
          setLastNames(response.data);
        } else {
          logger.warn("Unexpected response format:", response.data);
          setLastNames([]);
        }
      } catch (error) {
        logger.error("Error fetching last names:", error);
        setLastNames([]);
      }
    };

    if (householdId) {
      fetchLastNames();
    }
  }, [householdId]);

  // Format the title based on last names
  const getTitle = () => {
    if (lastNames.length === 0) return "Lifetime Financial Analysis";
    if (lastNames.length === 1)
      return `${lastNames[0]} Household Lifetime Financial Analysis`;
    return `${lastNames.join(" & ")} Household Lifetime Financial Analysis`;
  };

  const toggle = (key) => {
    setVisible((v) => ({ ...v, [key]: !v[key] }));
  };

  // Aggregate monthly data into annual data
  const aggregateToAnnual = (monthlyData) => {
    const annualData = {};

    monthlyData.forEach((point) => {
      const year = moment(point.date).format("YYYY");
      if (!annualData[year]) {
        annualData[year] = {
          date: `${year}-01-01`,
          income: 0,
          expenses: 0,
          surplus: 0,
          // Take the last value of the year for balance sheet items
          assets: point.assets,
          debts: point.debts,
          netWorth: point.netWorth,
          ciLower: point.ciLower,
          ciUpper: point.ciUpper,
        };
      }

      // Sum flow items
      annualData[year].income += point.income;
      annualData[year].expenses += point.expenses;
      annualData[year].surplus += point.surplus;

      // Update balance sheet items with latest values
      const currentMonth = moment(point.date).month();
      if (currentMonth === 11) {
        // December
        annualData[year].assets = point.assets;
        annualData[year].debts = point.debts;
        annualData[year].netWorth = point.netWorth;
        annualData[year].ciLower = point.ciLower;
        annualData[year].ciUpper = point.ciUpper;
      }
    });

    return Object.values(annualData);
  };

  // Generate timeline data
  const timelineData = useMemo(() => {
    const start = moment().subtract(24, "months");
    const end = moment().add(10, "years");
    const monthlyPoints = [];
    const baseIncome = 10000;
    const baseExpenses = 7000;
    const baseAssets = 300000;
    const baseDebts = 150000;
    let prevAssets = baseAssets;
    let prevDebts = baseDebts;

    // helper for Gaussian noise
    const randNormal = (mean, sd) => {
      let u = 0,
        v = 0;
      while (u === 0) u = Math.random();
      while (v === 0) v = Math.random();
      return (
        Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v) * sd + mean
      );
    };

    for (
      let cur = start.clone();
      cur.isSameOrBefore(end);
      cur.add(1, "month")
    ) {
      const m = cur.diff(start, "months");
      const isPast = cur.isBefore(moment(), "day");

      // Income: 3% annual drift; past ±10% noise, future ±3% noise
      const incDrift = 1 + (0.03 / 12) * m;
      const incNoise = isPast ? randNormal(0, 0.1) : randNormal(0, 0.03);
      const income = baseIncome * incDrift * (1 + incNoise);

      // Expenses: 2% annual drift; past ±10% noise, future ±3% noise
      const expDrift = 1 + (0.02 / 12) * m;
      const expNoise = isPast ? randNormal(0, 0.1) : randNormal(0, 0.03);
      let expenses = baseExpenses * expDrift * (1 + expNoise);

      // Simulate debt schedule: pay down over 5 years, plus a 50k new loan at month 36
      let debts;
      if (m < 60) {
        debts = baseDebts * (1 - m / 60);
        if (m === 36) debts += 50000;
      } else {
        debts = baseDebts * 0.0 + 50000; // small residual
      }

      // If debts drop to zero, drop monthly expense by average payment
      // BUT don't eliminate all expenses - only reduce by the debt payment portion
      if (prevDebts > debts) {
        const payment = prevDebts - debts;
        // Only reduce expenses by the payment amount, keeping base expenses intact
        expenses = Math.max(baseExpenses * 0.7, expenses - payment);
      }

      // Assets: random-walk past volatility, then smoother projected growth
      const surplus = income - expenses;
      let assets;
      if (isPast) {
        // past returns: mean 0.5%/mo, sd 3%
        const monthlyRet = randNormal(0.005, 0.03);
        assets = Math.max(0, prevAssets * (1 + monthlyRet) + surplus);
      } else {
        // future: steady 0.3%/mo drift + light noise
        const projRet = 0.003 + randNormal(0, 0.005);
        assets = Math.max(0, prevAssets * (1 + projRet) + surplus);
      }

      const netWorth = assets - debts;

      monthlyPoints.push({
        date: cur.format("YYYY-MM-DD"),
        income,
        expenses,
        surplus,
        assets,
        debts: -debts,
        netWorth,
        ciLower: isPast ? null : netWorth * 0.9,
        ciUpper: isPast ? null : netWorth * 1.1,
      });

      prevAssets = assets;
      prevDebts = debts;
    }

    return timeUnit === "year"
      ? aggregateToAnnual(monthlyPoints)
      : monthlyPoints;
  }, [timeUnit]);

  // Calculate today's percentage position
  useEffect(() => {
    if (!timelineData?.length) return;
    // find first point at-or-after today
    const idx = timelineData.findIndex((d) =>
      moment(d.date).isSameOrAfter(moment(), "day"),
    );
    const i = idx < 0 ? timelineData.length - 1 : idx;
    const pct = i / (timelineData.length - 1);
    setTodayPct(pct);
  }, [timelineData]);

  // Calculate axis ranges for perfect zero alignment
  const cashVals = timelineData.flatMap((d) => [
    d.income,
    -d.expenses,
    d.surplus,
  ]);
  const balVals = timelineData.flatMap((d) => [d.assets, d.debts, d.netWorth]);

  // Helper to round to nice numbers
  const roundToNice = (value, roundTo) => {
    return Math.ceil(Math.abs(value) / roundTo) * roundTo * Math.sign(value);
  };

  // Calculate nice round ranges for both axes, ensuring 0 is included
  const cashMin = Math.min(0, roundToNice(Math.min(...cashVals), 5000));
  const cashMax = Math.max(0, roundToNice(Math.max(...cashVals), 5000));
  const balMinData = Math.min(0, roundToNice(Math.min(...balVals), 250000));
  const balMaxData = Math.max(0, roundToNice(Math.max(...balVals), 250000));

  // Figure out the "zero-percent" position on the cash axis
  const zeroPct = -cashMin / (cashMax - cashMin);

  // Solve for balance axis bounds maintaining zero alignment
  const balMin = Math.min(
    0,
    roundToNice((-zeroPct * balMaxData) / (1 - zeroPct), 250000),
  );
  const balMax = Math.max(0, balMaxData);

  // Dataset definitions with correct data based on time unit
  const allDatasets = [
    {
      key: "income",
      type: "bar",
      label: timeUnit === "year" ? "Annual Income" : "Monthly Income",
      data: timelineData.map((d) => d.income),
      backgroundColor: COLORS.income,
      yAxisID: "cash",
      order: 3,
    },
    {
      key: "expenses",
      type: "bar",
      label: timeUnit === "year" ? "Annual Expenses" : "Monthly Expenses",
      data: timelineData.map((d) => -d.expenses),
      backgroundColor: COLORS.expenses,
      yAxisID: "cash",
      order: 2,
    },
    {
      key: "surplus",
      type: "bar",
      label: timeUnit === "year" ? "Annual Surplus" : "Monthly Surplus",
      data: timelineData.map((d) => d.surplus),
      backgroundColor: COLORS.surplus,
      yAxisID: "cash",
      order: 1,
    },
    {
      key: "assets-fill",
      type: "line",
      label: "Assets (area)",
      data: timelineData.map((d) => Math.max(0, d.assets)), // clamp to zero
      backgroundColor: COLORS.assetsFill,
      borderColor: "transparent",
      fill: {
        target: "origin",
        above: COLORS.assetsFill, // only fill above zero
        below: "transparent",
      },
      yAxisID: "balance",
      order: 0,
    },
    {
      key: "assets",
      type: "line",
      label: "Assets",
      data: timelineData.map((d) => d.assets),
      borderColor: COLORS.assets,
      borderDash: [5, 5],
      borderWidth: 2,
      pointRadius: 0,
      fill: false,
      yAxisID: "balance",
      order: 4,
    },
    {
      key: "debts-fill",
      type: "line",
      label: "Debts (area)",
      data: timelineData.map((d) => d.debts),
      backgroundColor: COLORS.debtsFill,
      borderColor: "transparent",
      fill: {
        target: "origin",
        above: "transparent",
        below: COLORS.debtsFill, // only fill below zero
      },
      yAxisID: "balance",
      order: 0,
    },
    {
      key: "debts",
      type: "line",
      label: "Debts",
      data: timelineData.map((d) => d.debts),
      borderColor: COLORS.debts,
      borderDash: [2, 2],
      borderWidth: 1,
      pointRadius: 0,
      fill: false,
      yAxisID: "balance",
      order: 5,
    },
    {
      key: "netWorth",
      type: "line",
      label: "Net Worth",
      data: timelineData.map((d) => d.netWorth),
      borderColor: COLORS.netWorth,
      borderWidth: 3,
      pointRadius: 0,
      fill: false,
      yAxisID: "balance",
      order: 6,
    },
    {
      key: "ci-lower",
      type: "line",
      label: "",
      data: timelineData.map((d) => d.ciLower),
      borderColor: "transparent",
      backgroundColor: COLORS.confidence,
      fill: "+1", // fill to next dataset (ci-upper)
      pointRadius: 0,
      yAxisID: "balance",
      order: 0,
    },
    {
      key: "ci-upper",
      type: "line",
      label: "",
      data: timelineData.map((d) => d.ciUpper),
      borderColor: "transparent",
      fill: false,
      pointRadius: 0,
      yAxisID: "balance",
      order: 0,
    },
  ];

  // apply visibility filter
  const datasets = allDatasets.filter((ds) => {
    // area fills always visible if their line is visible
    if (ds.key.endsWith("-fill")) {
      const parent = ds.key.split("-")[0];
      return visible[parent];
    }
    return visible[ds.key];
  });

  const chartData = {
    labels: timelineData.map((d) => d.date),
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        type: "time",
        time: {
          unit: timeUnit,
          displayFormats: {
            month: "MMM YY",
            year: "YYYY",
          },
          tooltipFormat: timeUnit === "month" ? "MMM YYYY" : "YYYY",
        },
        min:
          zoomLevel === "year"
            ? moment().subtract(12, "months").toDate()
            : undefined,
        max: zoomLevel === "year" ? moment().toDate() : undefined,
      },
      cash: {
        position: "left",
        min: cashMin,
        max: cashMax,
        title: { display: true, text: "Cash Flow ($)" },
        grid: { color: "rgba(0,0,0,0.1)" },
        ticks: {
          callback: (val) => `$${Math.round(val).toLocaleString()}`,
          stepSize: timeUnit === "year" ? 20000 : 5000,
          autoSkip: false,
          maxTicksLimit: 8,
          includeBounds: true,
          beginAtZero: true,
          // Force inclusion of zero tick
          include: [0],
        },
      },
      balance: {
        position: "right",
        min: balMin,
        max: balMax,
        title: { display: true, text: "Balance Sheet ($)" },
        grid: { display: false },
        ticks: {
          callback: (val) => `$${Math.round(val).toLocaleString()}`,
          stepSize: 250000,
          autoSkip: false,
          maxTicksLimit: 8,
          includeBounds: true,
          beginAtZero: true,
          // Force inclusion of zero tick
          include: [0],
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: false, // show colored boxes, not grey circles
          boxWidth: 20,
        },
        onClick: (e, item, legend) => {
          toggle(item.text.toLowerCase());
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const val = ctx.raw;
            const prefix = timeUnit === "year" ? "Annual " : "Monthly ";
            return `${prefix}${ctx.dataset.label}: $${Math.abs(val).toLocaleString()}`;
          },
        },
      },
      annotation: {
        annotations: {
          // Today vertical line
          todayLine: {
            type: "line",
            xMin: moment().toDate(),
            xMax: moment().toDate(),
            borderColor: "#718096",
            borderWidth: 2,
            borderDash: [6, 6],
            drawTime: "beforeDatasetsDraw",
          },
          // Zero line
          zeroLine: {
            type: "line",
            yScaleID: "cash", // anchor on the cash axis
            yMin: 0,
            yMax: 0,
            borderColor: "#718096",
            borderWidth: 1,
            borderDash: [4, 4],
            drawTime: "beforeDatasetsDraw",
          },
          // Goal markers
          ...goals.map((g, i) => ({
            type: "point",
            xValue: g.target_date,
            yValue: 0,
            radius: 6,
            borderColor: "#2D3748",
            backgroundColor: "#4A5568",
            label: {
              content: g.name,
              enabled: true,
              position: "top",
            },
          })),
        },
      },
    },
    animation: { duration: 800 },
  };

  return (
    <Card className="p-4 mb-6">
      <Typography variant="h5" component="h2" className="font-semibold mb-4">
        {getTitle()}
      </Typography>
      {/* Controls row */}
      <Box className="flex justify-between items-center gap-2 mb-4">
        {/* Time unit toggle */}
        <ToggleButtonGroup
          value={timeUnit}
          exclusive
          onChange={(e, newUnit) => {
            if (newUnit !== null) {
              setTimeUnit(newUnit);
            }
          }}
          size="small"
        >
          <ToggleButton value="month">Monthly</ToggleButton>
          <ToggleButton value="year">Annual</ToggleButton>
        </ToggleButtonGroup>

        {/* Zoom controls */}
        <Box className="flex gap-2">
          <MuiTooltip title="Last 12 months">
            <IconButton
              color={zoomLevel === "year" ? "primary" : "default"}
              onClick={() => setZoomLevel("year")}
            >
              <ZoomInIcon />
            </IconButton>
          </MuiTooltip>
          <MuiTooltip title="All time">
            <IconButton
              color={zoomLevel === "all" ? "primary" : "default"}
              onClick={() => setZoomLevel("all")}
            >
              <RestoreIcon />
            </IconButton>
          </MuiTooltip>
        </Box>
      </Box>

      {/* Toggles */}
      <FormGroup row className="mb-4">
        {["income", "expenses", "surplus", "assets", "debts", "netWorth"].map(
          (key) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox checked={visible[key]} onChange={() => toggle(key)} />
              }
              label={key.charAt(0).toUpperCase() + key.slice(1)}
            />
          ),
        )}
      </FormGroup>

      {/* Chart */}
      <Box className="h-[400px]">
        <Chart ref={chartRef} type="bar" data={chartData} options={options} />
      </Box>

      {/* Past/Future labels */}
      <Box className="-mt-1 px-18 flex justify-between mb-1">
        <Typography variant="caption" className="text-gray-600 dark:text-gray-400 font-semibold">
          ← Past (estimated)
        </Typography>
        <Typography variant="caption" className="text-gray-600 dark:text-gray-400 font-semibold">
          Future (projected) →
        </Typography>
      </Box>
    </Card>
  );
}
